/**
 * Whether the sidebar is folded to its icon rail.
 *
 * Folded by default: the rail leaves the page the most room. The user's
 * choice persists in localStorage ("sidebarCollapsed"). /map shows
 * the rail without touching that choice: a toggle there lasts until you leave
 * the map. "[" toggles, except while typing in a field or with a modifier
 * held. Storage can throw (private windows, blocked site data); the choice
 * then lasts for the session.
 */
import { useCallback, useEffect, useState } from "react";

export const STORAGE_KEY = "sidebarCollapsed";

function readStored() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
}

function writeStored(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Storage blocked: the choice lasts for this session.
  }
}

export function isEditableTarget(target) {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest("input, textarea, select, [contenteditable='true'], [contenteditable='']"),
  );
}

export function useSidebarCollapsed(pathname) {
  const onMap = pathname === "/map";
  const [stored, setStored] = useState(readStored);
  const [openOnMap, setOpenOnMap] = useState(false);

  useEffect(() => {
    if (!onMap) setOpenOnMap(false);
  }, [onMap]);

  const collapsed = onMap ? !openOnMap : stored;

  const toggle = useCallback(() => {
    if (onMap) {
      setOpenOnMap((open) => !open);
      return;
    }
    const next = !stored;
    writeStored(next);
    setStored(next);
  }, [onMap, stored]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "[" || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;
      toggle();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  return { collapsed, toggle };
}
